import { Canonical } from 'components/Basic/Head/Canonical/Canonical';
import { B2bFavicons } from 'components/Blocks/Favicons/B2bFavicons';
import { B2cAndWebFavicons } from 'components/Blocks/Favicons/B2cAndWebFavicons';
import CommonLayout from 'components/Layout/CommonLayout';
import { Error500WithBoundary } from 'components/Pages/ErrorPage/500/Error500';
import { Error503 } from 'components/Pages/ErrorPage/503/Error503';
import { CookiesStoreProvider } from 'context/CookieStoreProvider/CookiesStoreProvider';
import { DomainConfigProvider } from 'context/DomainConfigProvider/DomainConfigProvider';
import PageContentProvider from 'context/PageContentProvider/PageContentProvider';
import ShopsysGlobalProvider from 'context/ShopsysGlobalProvider/ShopsysGlobalProvider';
import { extend, locale } from 'dayjs';
import 'dayjs/locale/cs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/hu';
import 'dayjs/locale/it';
import 'dayjs/locale/pl';
import 'dayjs/locale/pt';
import 'dayjs/locale/ru';
import 'dayjs/locale/sk';
import 'dayjs/locale/zh';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { B2C_CZ_DOMAIN_ID, isB2bDomain, isDomain } from 'helpers/isDomainLocale';
import { isEnvironment } from 'helpers/isEnvironment';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import i18nConfig from 'i18n';
import appWithI18n from 'next-translate/appWithI18n';
import NextApp, { AppContext, AppInitialProps, AppProps as NextAppProps } from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { Roboto } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import 'nprogress/nprogress.css';
import { ReactElement } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { ErrorBoundary } from 'react-error-boundary';
import 'react-loading-skeleton/dist/skeleton.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import 'styles/globals.css';
import { NextPageExtended } from 'types/global';
import getGtmHeadScript from 'utils/Gtm/GtmHeadScript';

const UrqlWrapper = dynamic(() => import('components/Layout/UrqlWrapper').then((component) => component.UrqlWrapper));
const SupportChat = dynamic(
    () => import('components/Blocks/SupportChat/SupportChat').then((component) => component.SupportChat),
    { ssr: false },
);

extend(LocalizedFormat);

const robotoFont = Roboto({
    weight: ['400', '500', '700'],
    subsets: ['latin-ext', 'latin'],
    variable: '--font-roboto',
});

const {
    publicRuntimeConfig: { showSymfonyToolbar },
} = getConfig();

const SymfonyDebugToolbar =
    isEnvironment('development') &&
    showSymfonyToolbar === '1' &&
    dynamic(
        () =>
            import('components/Basic/SymfonyDebugToolbar/SymfonyDebugToolbar').then(
                (component) => component.SymfonyDebugToolbar,
            ),
        {
            ssr: true,
        },
    );

type AppProps = {
    Component: NextPageExtended;
    pageProps: any;
    err?: {
        message: string;
        name: string;
        statusCode?: number;
    };
} & Omit<NextAppProps, 'pageProps' | 'Component'>;

function MyApp({ Component, err, pageProps }: AppProps): ReactElement {
    const router = useRouter();
    const displaySupportChat = isDomain(pageProps.domainConfig.domainId, B2C_CZ_DOMAIN_ID);

    locale(pageProps.domainConfig.defaultLocale);

    useBroadcastChannel('reloadPage', () => {
        router.reload();
    });

    return (
        <DomainConfigProvider domainConfig={pageProps.domainConfig}>
            <ShopsysGlobalProvider>
                <ErrorBoundary
                    FallbackComponent={Error500WithBoundary}
                    onError={(error, info) => {
                        // eslint-disable-next-line no-console
                        console.error('ErrorBoundary caught:', error);
                        // eslint-disable-next-line no-console
                        console.error('Component stack:', info.componentStack);
                    }}
                >
                    <CookiesStoreProvider cookieStoreStateFromServer={pageProps.cookiesStore}>
                        <Head key={isB2bDomain(pageProps.domainConfig) ? 'b2b' : 'b2c'}>
                            <meta content="Tescoma" name="apple-mobile-web-app-title" />
                            <meta content="Tescoma" name="application-name" />
                            <meta content="#bd0d2e" name="theme-color" />
                            <meta content="#bd0d2e" name="msapplication-TileColor" />

                            {isB2bDomain(pageProps.domainConfig) ? (
                                <>
                                    <meta content="noindex,nofollow" name="robots" />
                                    <B2bFavicons key="b2b-favicons" />
                                </>
                            ) : (
                                <B2cAndWebFavicons key="b2c-web-favicons" />
                            )}
                        </Head>
                        {getGtmHeadScript()} {displaySupportChat && <SupportChat />}
                        <style global jsx>{`
                            html {
                                font-family: ${robotoFont.style.fontFamily};
                            }
                        `}</style>
                        <Canonical url={pageProps.domainConfig.url} />
                        <UrqlWrapper pageProps={pageProps}>
                            <ToastContainer autoClose={6000} position="top-center" theme="colored" />
                            <PageContentProvider pageProps={pageProps}>
                                {Component.layout === 'plain' ? (
                                    <Component {...pageProps} err={pageProps.err ?? err} />
                                ) : (
                                    <CommonLayout
                                        showLastVisitedProducts={Component.layout === 'withLastVisitedProducts'}
                                    >
                                        {pageProps.isMaintenance ? (
                                            <Error503 />
                                        ) : (
                                            <Component {...pageProps} err={pageProps.err ?? err} />
                                        )}
                                    </CommonLayout>
                                )}
                            </PageContentProvider>
                        </UrqlWrapper>
                        {SymfonyDebugToolbar && <SymfonyDebugToolbar />}
                    </CookiesStoreProvider>
                </ErrorBoundary>
            </ShopsysGlobalProvider>
        </DomainConfigProvider>
    );
}

// eslint-disable-next-line
// @ts-ignore
const MyAppWithI18n = appWithI18n(MyApp, { ...i18nConfig });

const setErrorAwareInitialProps = (
    AppComponent: unknown,
    getInitialProps: (appContext: AppContext) => Promise<AppInitialProps & { err?: AppProps['err'] }>,
) => {
    (AppComponent as { getInitialProps: typeof getInitialProps }).getInitialProps = getInitialProps;
};

setErrorAwareInitialProps(MyAppWithI18n, async (appContext) => {
    const appInitialProps = await NextApp.getInitialProps(appContext);
    const pageError = appInitialProps.pageProps.err;

    return {
        ...appInitialProps,
        err: appContext.ctx.err
            ? {
                  message: appContext.ctx.err.message,
                  name: appContext.ctx.err.name,
                  statusCode: appContext.ctx.err.statusCode,
              }
            : pageError
              ? {
                    message: typeof pageError === 'string' ? pageError : pageError.message,
                    name: typeof pageError === 'string' ? 'Error' : pageError.name,
                    statusCode: appInitialProps.pageProps.statusCode,
                }
              : undefined,
    };
});

export default MyAppWithI18n;
